import React, { useEffect } from 'react';

// Component imports
import { CreateGoalModal } from '../../component/modal/CreateGoalModal';
import { Do } from '../../component/modal/DoModal';
import { PlanRuleModal } from '../../component/modal/PlanRuleModal';
import GoalsDataGrid from '../../component/DataGrid/GoalsDataGrid';
import { GoalAccordion } from '../../component/Accordion/GoalAccordion';

// Context imports
import { useRuleContext } from '../../context/RuleStore';
import { useUserContext } from '../../context/UserStore';

// Material-UI imports
import { Button, Card, CardContent, CardHeader, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for animations
const FastFloatingElement = styled('div')(({ theme }) => ({
  animation: 'fastFloat 3s ease-in-out infinite',
  
  '&:nth-of-type(2n)': {
    animationDelay: '0.5s',
  },
  '&:nth-of-type(3n)': {
    animationDelay: '1s',
  },
  '&:nth-of-type(4n)': {
    animationDelay: '1.5s',
  },
  
  '@keyframes fastFloat': {
    '0%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-20px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    }
  }
}));

// Goals section styled card with gradient animation
const GradientGoalsCard = styled(Card)(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  flexWrap: 'wrap',
  width: '98%', // Much wider to fill almost the entire container
  minHeight: '300px', // Increased height
  // Orange to red gradient
  background: 'linear-gradient(to bottom, #ff9a56 0%, #ff6b6b 25%, #feca57 50%, #ff6b6b 75%, #ff9a56 100%)',
  backgroundSize: '100% 400%',
  animation: 'orangeRedVertical 5s ease infinite',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  
  '& .MuiCardContent-root': {
    background: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '8px',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
  },
  
  // Make text white for better contrast
  '& .MuiTypography-root': {
    color: 'white',
  },
  
  '@keyframes orangeRedVertical': {
    '0%': {
      backgroundPosition: '0% 0%',
    },
    '50%': {
      backgroundPosition: '0% 100%',
    },
    '100%': {
      backgroundPosition: '0% 0%',
    }
  }
}));

// Goal accordion container with black to gray gradient (for current goal)
const GradientAccordionContainer = styled('div')(({ theme }) => ({
  width: '98%', // Much wider to match the Goals card
  maxWidth: '98%',
  margin: '0 auto',
  padding: '20px',
  borderRadius: '8px',
  boxSizing: 'border-box',
  // Black to gray gradient
  background: 'linear-gradient(to bottom, #000000 0%, #404040 25%, #808080 50%, #404040 75%, #000000 100%)',
  backgroundSize: '100% 400%',
  animation: 'blackGrayVertical 5s ease infinite',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  
  '@keyframes blackGrayVertical': {
    '0%': {
      backgroundPosition: '0% 0%',
    },
    '50%': {
      backgroundPosition: '0% 100%',
    },
    '100%': {
      backgroundPosition: '0% 0%',
    }
  }
}));

export const GoalsView = () => {
  const { state: ruleState, action: ruleActions } = useRuleContext();
  const { state: userState, action: userActions } = useUserContext();
  useEffect(() => {
    userActions.getGoalsAsyncAction();
  }, []);
  useEffect(() => {
    ruleActions.getRulesAsyncAction();
  }, []);
  
  console.log(ruleState);
  
  return (
    <>
      {/* Page Header */}
      <FastFloatingElement style={{ animationDelay: '0.3s', textAlign: 'center', marginBottom: '20px' }}>
        <Typography variant="h3" component="h1" sx={{ 
          color: 'white', 
          fontWeight: 'bold',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
          marginBottom: '10px'
        }}>
          Goals & Planning
        </Typography>
        <Typography variant="h6" component="p" sx={{ 
          color: 'rgba(255,255,255,0.8)', 
          textShadow: '1px 1px 2px rgba(0,0,0,0.6)' 
        }}>
          Set ambitious goals, break them into actionable tasks, and track your progress
        </Typography>
      </FastFloatingElement>
      
      {/* Goals Summary Section */}
      <FastFloatingElement style={{ animationDelay: '0.6s' }}>
        <GradientGoalsCard sx={{ margin: 2 }}>
          <CardContent>
            <Typography variant="h4" component="div" sx={{ 
              fontWeight: 'bold', 
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              marginBottom: '20px'
            }}>
              Goals Summary
            </Typography>
            <Typography sx={{ fontSize: 18, marginBottom: '20px', lineHeight: 1.6 }} color="white">
              Transform your aspirations into reality by setting clear, achievable goals. Break down complex objectives into manageable tasks and track your journey toward success.
            </Typography>
            <Typography sx={{ fontSize: 16, fontStyle: 'italic', opacity: 0.9 }} color="white">
              Plan smart, execute consistently, and celebrate your progress along the way.
            </Typography>
          </CardContent>
        </GradientGoalsCard>
      </FastFloatingElement>
      
      {/* Current Goal Section */}
      <FastFloatingElement style={{ animationDelay: '0.9s' }}>
        <GradientAccordionContainer>
          <GoalAccordion notes={userState.currentGoal.notes} tasks={userState.currentGoal.tasks} currentGoal={true} goalName={undefined}/>
        </GradientAccordionContainer>
      </FastFloatingElement>
      
      {/* Goal Creation Modal */}
      <FastFloatingElement style={{ animationDelay: '1.2s' }}>
        <CreateGoalModal />
      </FastFloatingElement>
      
      {/* Goal History Section - Just Accordions */}
      <FastFloatingElement style={{ animationDelay: '1.5s' }}>
        <Typography variant="h5" component="div" sx={{ 
          fontWeight: 'bold', 
          color: 'white',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          Goal History & Completion Status
        </Typography>
        <GoalsDataGrid />
      </FastFloatingElement>
      
      {/* Commented out rule planning section */}
      {/* {ruleState?.rules?.filter((item)=>{return item.points}).map((rule) => {
    return (
              <div key={rule.id} style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                <span key={`${rule.name}`}>
                  {rule.name}:&nbsp;&nbsp;{rule.points}
                </span>
                &nbsp;&nbsp;&nbsp;
                <PlanRuleModal/>
              </div>
            );
          })} */}
    </>
  );
};
